// Portfolio › a company (FOUNDER-OS; 12 "drill-down is a click, not the default"): the header
// (name, relation, health) with "Open in company", and four read-only tabs — Overview (its Focus
// row in full), Chart (its org chart), KPIs (its company scorecard) and Priorities (its quarter).
// Chart, KPIs and Priorities read the company's own routes, through the founder's own membership
// or agency grant there; nothing here edits.
import {
  Badge,
  LocationHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BuildingIcon, ExternalLinkIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { FocusRow } from "../../api/types";
import { currentWeek } from "../../focus/weeks";
import { HealthDot } from "../../shell/company-switcher";
import type { CompanyAccess } from "../company";
import {
  AWAITING_DATA,
  formatAttainment,
  formatCardMetric,
  formatDateTime,
  formatScore,
} from "../format";
import { LinkButton } from "../ops-ux";
import { prioritiesQuery } from "../priorities/priorities-screen";
import { companyScorecardQuery } from "../queries";
import { LoadError, Loading, Nothing } from "../screen-parts";
import { featureState, useSession } from "../use-feature";
import { CompanyOrgTree } from "./portfolio-chart";
import {
  BudgetBar,
  Figure,
  formatSpend,
  headcountText,
  KpiCell,
  NeedsChips,
  RelationBadge,
  relativeDay,
  useAgencyNames,
} from "./portfolio-parts";
import { focusQuery } from "./portfolio-queries";

const TABS = ["overview", "chart", "kpis", "priorities"] as const;
type Tab = (typeof TABS)[number];

export function CompanyDrilldown({
  api,
  company,
  companyId,
}: {
  api: Api;
  company: CompanyAccess;
  companyId: string;
}) {
  const focus = useQuery(focusQuery(api, company.id));
  const agencies = useAgencyNames();
  const [tab, setTab] = useState<Tab>("overview");
  const row = focus.data?.companies.find((r) => r.company_id === companyId);
  const crumbs = [
    {
      label: "Portfolio",
      render: <Link to="/c/$slug/portfolio" params={{ slug: company.slug }} />,
    },
  ];
  if (focus.isPending)
    return (
      <div className="flex min-w-0 flex-1 flex-col">
        <LocationHeader icon={BuildingIcon} crumbs={crumbs} title="Company" />
        <div className="px-6 py-5">
          <Loading label="Loading the company" />
        </div>
      </div>
    );
  if (focus.isError || !row)
    return (
      <div className="flex min-w-0 flex-1 flex-col">
        <LocationHeader icon={BuildingIcon} crumbs={crumbs} title="Company" />
        <div className="px-6 py-5">
          {focus.isError ? (
            <LoadError what="The company" error={focus.error} />
          ) : (
            <Nothing title="This company is not in the portfolio" />
          )}
        </div>
      </div>
    );
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="portfolio-drilldown">
      <LocationHeader
        icon={BuildingIcon}
        crumbs={crumbs}
        title={row.name}
        badge={
          <span className="flex items-center gap-2">
            <RelationBadge relation={row.relation} agency={agencies.get(row.company_id)} />
            <HealthDot band={row.band} />
          </span>
        }
        actions={
          <LinkButton
            variant="secondary"
            render={<Link to="/c/$slug/desk" params={{ slug: row.slug }} />}
          >
            <ExternalLinkIcon aria-hidden="true" />
            Open in company
          </LinkButton>
        }
      />
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="flex min-w-0 flex-col">
        <div className="px-6 max-md:px-4">
          <TabsList variant="line" aria-label={`${row.name} views`}>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="chart">Chart</TabsTrigger>
            <TabsTrigger value="kpis">KPIs</TabsTrigger>
            <TabsTrigger value="priorities">Priorities</TabsTrigger>
          </TabsList>
        </div>
        <div className="px-6 py-5 max-md:px-4">
          <TabsContent value="overview">
            <Overview row={row} />
          </TabsContent>
          <TabsContent value="chart">
            {tab === "chart" ? (
              <CompanyOrgTree api={api} companyId={row.company_id} name={row.name} />
            ) : null}
          </TabsContent>
          <TabsContent value="kpis">
            {tab === "kpis" ? <CompanyKpis api={api} row={row} /> : null}
          </TabsContent>
          <TabsContent value="priorities">
            {tab === "priorities" ? <CompanyPriorities api={api} row={row} /> : null}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

function Overview({ row }: { row: FocusRow }) {
  const p = row.priorities;
  return (
    <div className="flex flex-col gap-6" data-slot="drilldown-overview">
      {row.needs_you.length ? (
        <section aria-label="Needs you" className="flex flex-col gap-2">
          <h2 className="font-heading text-base font-semibold">Needs you</h2>
          <NeedsChips needs={row.needs_you} />
        </section>
      ) : (
        <p className="text-sm text-muted-foreground">Nothing needs you here: all quiet.</p>
      )}
      <div className="grid grid-cols-4 gap-5 max-lg:grid-cols-2">
        <Figure label={`KPI${row.kpi_period ? ` · ${row.kpi_period}` : ""}`}>
          <KpiCell row={row} />
        </Figure>
        <Figure label="Budget (month)">
          <BudgetBar finance={row.finance} />
        </Figure>
        <Figure label="Agents">
          {row.agents.des} DEs · {row.agents.live} live · {row.agents.runs_7d} runs 7d ·{" "}
          {formatSpend(row.agents.spend_month_micros)} this month
          {row.agents.approvals_waiting ? ` · ${row.agents.approvals_waiting} to approve` : ""}
        </Figure>
        <Figure label="Headcount">{headcountText(row.headcount)}</Figure>
        <Figure label="Next meeting">
          {row.meetings.next_at
            ? `${relativeDay(row.meetings.next_at)} (${formatDateTime(row.meetings.next_at)})`
            : "None scheduled"}
        </Figure>
        <Figure label="Last meeting">
          {row.meetings.last_at ? relativeDay(row.meetings.last_at) : "None yet"}
        </Figure>
        <Figure label={`Priorities${p.quarter ? ` · ${p.quarter}` : ""}`}>
          {p.on} on track · {p.at_risk} at risk · {p.off} off · {p.achieved} achieved
        </Figure>
      </div>
      <section aria-labelledby="off-track" className="flex flex-col gap-2">
        <h2 id="off-track" className="font-heading text-base font-semibold">
          Off-track priorities
        </h2>
        {p.off_track.length ? (
          <ul className="flex flex-col divide-y divide-border-subtle rounded-md border border-border">
            {p.off_track.map((g) => (
              <li key={g.goal_id} className="flex items-center gap-2 px-3 py-2 text-sm">
                <Badge variant={g.track === "off" ? "danger" : "warning"}>
                  {g.track === "off" ? "Off track" : "At risk"}
                </Badge>
                <span className="min-w-0 flex-1 truncate font-medium">{g.title}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {[g.seat_title, g.owner_name].filter(Boolean).join(" · ")}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Every priority is on track.</p>
        )}
      </section>
      <section aria-labelledby="escalated" className="flex flex-col gap-2">
        <h2 id="escalated" className="font-heading text-base font-semibold">
          Escalated to you
        </h2>
        {row.escalations.items.length ? (
          <ul className="flex flex-col divide-y divide-border-subtle rounded-md border border-border">
            {row.escalations.items.map((e) => (
              <li key={e.issue_id} className="flex flex-col gap-0.5 px-3 py-2 text-sm">
                <span className="font-medium">{e.title}</span>
                {e.why ? <span className="text-xs text-muted-foreground">{e.why}</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No issue is escalated to you.</p>
        )}
      </section>
    </div>
  );
}

function CompanyKpis({ api, row }: { api: Api; row: FocusRow }) {
  const { me } = useSession();
  const on = featureState(me, row.company_id, "kpi").enabled;
  const week = currentWeek();
  const card = useQuery({ ...companyScorecardQuery(api, row.company_id, week), enabled: on });
  if (!on)
    return (
      <Nothing title="No scorecard here">
        The KPI add-on is off for {row.name}; its band still rises to Focus from its projections.
      </Nothing>
    );
  if (card.isPending) return <Loading label={`Loading ${row.name}'s scorecard`} />;
  if (card.isError) return <LoadError what={`${row.name}'s scorecard`} error={card.error} />;
  const s = card.data;
  return (
    <div className="flex flex-col gap-3" data-slot="drilldown-kpis">
      <p className="text-sm text-muted-foreground">
        Week {week} · KPI score{" "}
        <span className="font-medium text-foreground">{formatScore(s.kpi_score)}</span>
      </p>
      {s.metrics.length === 0 ? (
        <Nothing title="No metrics yet" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Metric</TableHead>
              <TableHead className="text-right">Actual</TableHead>
              <TableHead className="text-right">Target</TableHead>
              <TableHead>State</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {s.metrics.map((m) => (
              <TableRow key={m.metric_def_id}>
                <TableCell className="font-medium">{m.name}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {m.actual === null ? AWAITING_DATA : formatCardMetric(m.actual, m.unit)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {m.target === null ? "—" : formatCardMetric(m.target, m.unit)}
                </TableCell>
                <TableCell>
                  {m.attainment === null ? (
                    <Badge>{m.state.replace(/_/g, " ")}</Badge>
                  ) : (
                    <Badge variant={m.attainment >= 1 ? "success" : "danger"}>
                      {m.attainment >= 1 ? "Hit" : "Miss"} · {formatAttainment(m.attainment)}
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function CompanyPriorities({ api, row }: { api: Api; row: FocusRow }) {
  const board = useQuery(prioritiesQuery(api, row.company_id, undefined));
  if (board.isPending) return <Loading label={`Loading ${row.name}'s priorities`} />;
  if (board.isError) return <LoadError what={`${row.name}'s priorities`} error={board.error} />;
  const goals = board.data.data;
  if (goals.length === 0) return <Nothing title={`No priorities for ${board.data.quarter} yet`} />;
  return (
    <ul
      className="flex flex-col divide-y divide-border-subtle rounded-md border border-border"
      data-slot="drilldown-priorities"
      aria-label={`${row.name}'s priorities, ${board.data.quarter}`}
    >
      {goals.map((g) => {
        const done = g.milestones.filter((m) => m.done_at).length;
        return (
          <li key={g.id} className="flex items-center gap-2 px-3 py-2 text-sm">
            <Badge
              variant={
                g.state !== "open"
                  ? "neutral"
                  : g.track === "on"
                    ? "success"
                    : g.track === "off"
                      ? "danger"
                      : "warning"
              }
            >
              {g.state !== "open"
                ? g.state
                : g.track === "on"
                  ? "On track"
                  : g.track === "off"
                    ? "Off track"
                    : "At risk"}
            </Badge>
            <span className="min-w-0 flex-1 truncate font-medium">{g.title}</span>
            <span className="text-xs text-muted-foreground tabular-nums">
              {g.milestones.length ? `${done}/${g.milestones.length} milestones` : "No milestones"}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
